import { Fragment, ReactElement, useMemo } from 'react';
import {
  FamilyDetailNotificationsCrumbs,
  FamilyFatherDetailNotificationsCrumbs,
  FamilyMotherDetailNotificationsCrumbs,
  FamilyOtherDetailNotificationsCrumbs,
} from 'notifications-from-civil-status/constants/breadcrumb';
import { IdAmbito } from 'shell/constants/id-ambito';
import {
  FamilyDetailNotificationsSubscriptionCrumbs,
  FamilyFatherDetailNotificationsSubscriptionCrumbs,
  FamilyMotherDetailNotificationsSubscriptionCrumbs,
  FamilyOtherDetailNotificationsSubscriptionCrumbs,
} from 'subscription/constants/breadcrumb';
import {
  FamilyDetailNotificationsMutationCrumbs,
  FamilyFatherDetailNotificationsMutationCrumbs,
  FamilyMotherDetailNotificationsMutationCrumbs,
  FamilyOtherDetailNotificationsMutationCrumbs,
} from 'mutation/constants/research-notifications-civil-status.crumbs';
import {
  FamilyDetailNotificationsCancellationsCrumbs,
  FamilyFatherDetailNotificationsCancellationsCrumbs,
  FamilyMotherDetailNotificationsCancellationsCrumbs,
  FamilyOtherDetailNotificationsCancellationsCrumbs,
} from 'cancellations/constants/breadcrumb';
import { getBaseUrl } from 'notifications-from-civil-status/utils/get-base-url';
import { useParams } from 'react-router-dom';
import { useEventCallback } from '@mui/material';
import {
  FamilyDetailNotificationsCivilStatusCrumbs,
  FamilyFatherDetailNotificationsCivilStatusCrumbs,
  FamilyMotherDetailNotificationsCivilStatusCrumbs,
  FamilyOtherDetailNotificationsCivilStatusCrumbs,
} from 'civil-status/constants/breadcrumb';
import ResearchFamilyNotificationsDetailShared from 'shell/components/shared/research-notifications/research-family/detail';

interface ResearchFamilyNotificationsDetailCivilStatusSharedProps {
  scope?: string;
  isOther?: boolean;
  isMother?: boolean;
  isFather?: boolean;
}

const ResearchFamilyNotificationsDetailCivilStatusShared = ({
  scope,
  isOther,
  isMother,
  isFather,
}: ResearchFamilyNotificationsDetailCivilStatusSharedProps): ReactElement => {
  const params = useParams();

  const idCat = useMemo(() => {
    return params.idCat || undefined;
  }, [params.idCat]);

  const idNotifica = useMemo(() => {
    return params.idNotifica || '';
  }, [params.idNotifica]);

  const idAnsc = useMemo(() => {
    return params.id || undefined;
  }, [params.id]);

  const baseUrl: string = useMemo(() => {
    return getBaseUrl(scope, idCat);
  }, [scope, idCat]);

  const goBackUrl: string = useMemo(() => {
    return `${baseUrl}/modifica/${idAnsc}/${idNotifica}`;
  }, [baseUrl, idAnsc, idNotifica]);

  const goBackUrlDetail: string = useMemo(() => {
    return `${baseUrl}/visualizza/${idAnsc}/${idNotifica}`;
  }, [baseUrl, idAnsc, idNotifica]);

  const getItemsOther = useEventCallback(() => {
    if (idCat) {
      return FamilyOtherDetailNotificationsCivilStatusCrumbs(goBackUrl, idCat);
    }

    if (!scope) {
      return FamilyOtherDetailNotificationsCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return FamilyOtherDetailNotificationsSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return FamilyOtherDetailNotificationsMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return FamilyOtherDetailNotificationsCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  });

  const getItemsMother = useEventCallback(() => {
    if (idCat) {
      return FamilyMotherDetailNotificationsCivilStatusCrumbs(goBackUrl, idCat);
    }

    if (!scope) {
      return FamilyMotherDetailNotificationsCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return FamilyMotherDetailNotificationsSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return FamilyMotherDetailNotificationsMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return FamilyMotherDetailNotificationsCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  });

  const getItemsFather = useEventCallback(() => {
    if (idCat) {
      return FamilyFatherDetailNotificationsCivilStatusCrumbs(goBackUrl, idCat);
    }

    if (!scope) {
      return FamilyFatherDetailNotificationsCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return FamilyFatherDetailNotificationsSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return FamilyFatherDetailNotificationsMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return FamilyFatherDetailNotificationsCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  });

  const items = useMemo(() => {
    if (isOther) {
      return getItemsOther();
    }

    if (isMother) {
      return getItemsMother();
    }

    if (isFather) {
      return getItemsFather();
    }

    if (idCat) {
      return FamilyDetailNotificationsCivilStatusCrumbs(goBackUrl, idCat);
    }

    if (!scope) {
      return FamilyDetailNotificationsCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return FamilyDetailNotificationsSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return FamilyDetailNotificationsMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return FamilyDetailNotificationsCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  }, [scope, idCat, isOther, isMother, isFather]);

  return (
    <Fragment>
      <ResearchFamilyNotificationsDetailShared
        goBackUrl={goBackUrl}
        goBackUrlDetail={goBackUrlDetail}
        items={items}
        isFather={isFather}
        isMother={isMother}
        isOther={isOther}
        isFromCivilStatus={Boolean(idCat)}
      />
    </Fragment>
  );
};

ResearchFamilyNotificationsDetailCivilStatusShared.displayName =
  'Research Family Notifications CivilStatusShared component';

export default ResearchFamilyNotificationsDetailCivilStatusShared;
